import React, { useEffect, useState, useMemo } from "react";
import { Plus, Search, X } from "lucide-react";
import dayjs from "dayjs";

const API = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls = "bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const STATUS_STYLE = {
  ACTIVE:     "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/20",
  CLOSED:     "text-white/30 bg-white/5 border-white/10",
  FORECLOSED: "text-purple-400 bg-purple-500/10 border-purple-500/20",
  DEFAULTED:  "text-red-400 bg-red-500/10 border-red-500/20",
};

function fmt(n) { return n != null ? `₹${Number(n).toLocaleString("en-IN")}` : "—"; }

export default function LoansTab() {
  const [loans,    setLoans]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState("");
  const [statusF,  setStatusF]  = useState("ACTIVE");
  const [showAdd,  setShowAdd]  = useState(false);
  const [selected, setSelected] = useState(null); // loan for schedule view
  const [schedule, setSchedule] = useState([]);
  const [msg,      setMsg]      = useState(null);

  const [form, setForm] = useState({
    employee_id: "", loan_type: "Loan", principal: "", interest_rate: 0,
    tenure_months: 12, start_month: "", reason: "",
  });
  const [addBusy, setAddBusy] = useState(false);
  const token = localStorage.getItem("token");

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/payroll/loans`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      // GET /loans returns {"loans": [...]}, not a bare array.
      setLoans(Array.isArray(data) ? data : (Array.isArray(data?.loans) ? data.loans : []));
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const visible = useMemo(() => {
    let out = Array.isArray(loans) ? loans : [];
    if (statusF !== "ALL") out = out.filter(l => l.status === statusF);
    if (search) {
      const q = search.toLowerCase();
      out = out.filter(l => (l.employee_name || "").toLowerCase().includes(q) || (l.employee_id || "").toLowerCase().includes(q));
    }
    return out;
  }, [loans, statusF, search]);

  const addLoan = async () => {
    if (!form.employee_id || !form.principal || !form.start_month) {
      setMsg({ type: "error", text: "Employee ID, principal and start month are required." }); return;
    }
    setAddBusy(true); setMsg(null);
    try {
      const res = await fetch(`${API}/api/admin/payroll/loans`, {
        method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setMsg({ type: "success", text: "Loan created." });
      setShowAdd(false);
      setForm({ employee_id: "", loan_type: "Loan", principal: "", interest_rate: 0, tenure_months: 12, start_month: "", reason: "" });
      await load();
    } catch (e) { setMsg({ type: "error", text: e.message }); }
    finally { setAddBusy(false); }
  };

  const foreclose = async (id) => {
    try {
      await fetch(`${API}/api/admin/payroll/loans/${id}/foreclose`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
      setLoans(l => l.map(x => x.id === id ? { ...x, status: "FORECLOSED" } : x));
      setMsg({ type: "success", text: "Loan foreclosed." });
    } catch (e) { setMsg({ type: "error", text: e.message }); }
  };

  const loadSchedule = async (loan) => {
    setSelected(loan);
    try {
      const res = await fetch(`${API}/api/admin/payroll/loans/${loan.id}/schedule`, { headers: { Authorization: `Bearer ${token}` } });
      setSchedule(await res.json());
    } catch { setSchedule([]); }
  };

  return (
    <div className="space-y-5">

      {/* Summary chips */}
      {loans.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: "Active Loans",    value: loans.filter(l => l.status === "ACTIVE").length,                                   color: "#00ff90" },
            { label: "Total Outstanding",value: fmt(loans.filter(l => l.status === "ACTIVE").reduce((s, l) => s + (l.outstanding || 0), 0)), color: "#f87171" },
            { label: "Monthly EMI",     value: fmt(loans.filter(l => l.status === "ACTIVE").reduce((s, l) => s + (l.emi || 0), 0)),          color: "#fbbf24" },
          ].map(({ label, value, color }) => (
            <div key={label} className={`${card} p-4 text-center`}>
              <p className="text-xl font-bold" style={{ color }}>{value}</p>
              <p className="text-xs text-white/30 mt-0.5">{label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Loans table */}
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center gap-3 flex-wrap">
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Loan & Advance Register</span>
          <div className="ml-auto flex items-center gap-2">
            <Search size={13} className="text-white/30" />
            <input className={inputCls} placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} style={{ width: 150 }} />
            <select className={inputCls} value={statusF} onChange={e => setStatusF(e.target.value)}>
              {["ALL","ACTIVE","CLOSED","FORECLOSED","DEFAULTED"].map(s => <option key={s} value={s}>{s === "ALL" ? "All" : s}</option>)}
            </select>
            <button onClick={() => setShowAdd(v => !v)}
              className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
              <Plus size={12} /> New
            </button>
          </div>
        </div>

        {/* Add form */}
        {showAdd && (
          <div className="px-5 py-4 border-b border-white/5 bg-[#00ff90]/3 space-y-3">
            <p className="text-xs text-[#00ff90]/60 uppercase tracking-wider">New Loan / Advance</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div><label className={labelCls}>Employee ID</label><input className={inputCls} value={form.employee_id} onChange={e => setForm(f => ({...f, employee_id: e.target.value}))} placeholder="E001" /></div>
              <div><label className={labelCls}>Type</label>
                <select className={inputCls} value={form.loan_type} onChange={e => setForm(f => ({...f, loan_type: e.target.value}))}>
                  {["Loan","Advance","Festival Advance"].map(t => <option key={t}>{t}</option>)}
                </select></div>
              <div><label className={labelCls}>Principal (₹)</label><input type="number" className={inputCls} value={form.principal} onChange={e => setForm(f => ({...f, principal: e.target.value}))} /></div>
              <div><label className={labelCls}>Interest % (Annual)</label><input type="number" step="0.1" className={inputCls} value={form.interest_rate} onChange={e => setForm(f => ({...f, interest_rate: parseFloat(e.target.value)}))} /></div>
              <div><label className={labelCls}>Tenure (months)</label><input type="number" className={inputCls} value={form.tenure_months} onChange={e => setForm(f => ({...f, tenure_months: parseInt(e.target.value)}))} /></div>
              <div><label className={labelCls}>Start Month</label><input type="month" className={inputCls} value={form.start_month} onChange={e => setForm(f => ({...f, start_month: e.target.value}))} /></div>
              <div className="md:col-span-3"><label className={labelCls}>Reason</label><input className={inputCls} value={form.reason} onChange={e => setForm(f => ({...f, reason: e.target.value}))} placeholder="Reason for loan" /></div>
            </div>
            {msg && <p className={`text-xs ${msg.type === "error" ? "text-red-400" : "text-[#00ff90]"}`}>{msg.text}</p>}
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowAdd(false)} className="text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">Cancel</button>
              <button onClick={addLoan} disabled={addBusy}
                className="text-xs px-4 py-1.5 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors">
                {addBusy ? "Creating…" : "Create Loan"}
              </button>
            </div>
          </div>
        )}

        {msg && !showAdd && <p className={`px-5 py-3 text-xs ${msg.type === "error" ? "text-red-400" : "text-[#00ff90]"}`}>{msg.text}</p>}

        <div className="overflow-x-auto max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#0d0d0d]">
              <tr className="border-b border-white/5">
                {["Employee","Type","Principal","Outstanding","EMI","Start","Status",""].map(h => (
                  <th key={h} className="px-3 py-3 text-left text-xs text-[#00ff90]/40 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? Array.from({length:4}).map((_,i) => <tr key={i}><td colSpan={8} className="px-4 py-3"><div className="h-8 rounded bg-white/5 animate-pulse" /></td></tr>)
              : visible.length === 0 ? <tr><td colSpan={8} className="px-4 py-8 text-center text-white/20 italic text-sm">No loans found.</td></tr>
              : visible.map(l => (
                <tr key={l.id} className="border-b border-white/5 hover:bg-white/3 transition-colors">
                  <td className="px-3 py-3"><p className="text-sm text-white/70">{l.employee_name}</p><p className="text-xs text-white/30">{l.employee_id}</p></td>
                  <td className="px-3 py-3 text-white/50 text-xs">{l.loan_type}</td>
                  <td className="px-3 py-3 text-white/60 text-xs">{fmt(l.principal)}</td>
                  <td className="px-3 py-3 text-red-400/70 text-xs">{fmt(l.outstanding)}</td>
                  <td className="px-3 py-3 text-white/60 text-xs">{fmt(l.emi)}/mo</td>
                  <td className="px-3 py-3 text-white/30 text-xs">{l.start_month}</td>
                  <td className="px-3 py-3"><span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLE[l.status] || STATUS_STYLE.ACTIVE}`}>{l.status}</span></td>
                  <td className="px-3 py-3">
                    <div className="flex gap-1.5">
                      <button onClick={() => loadSchedule(l)} className="text-xs px-2 py-1 rounded-lg border border-[#00ff90]/20 bg-[#00ff90]/5 text-[#00ff90]/60 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">Schedule</button>
                      {l.status === "ACTIVE" && (
                        <button onClick={() => foreclose(l.id)} className="text-xs px-2 py-1 rounded-lg border border-purple-500/20 bg-purple-500/5 text-purple-400/60 hover:bg-purple-500/15 hover:text-purple-400 transition-colors">Foreclose</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Repayment schedule modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-lg rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] p-6 space-y-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-white">Repayment Schedule — {selected.employee_name}</p>
              <button onClick={() => setSelected(null)} className="text-white/40 hover:text-white transition-colors"><X size={16} /></button>
            </div>
            <div className="space-y-1.5">
              {schedule.length === 0 ? <p className="text-white/20 text-sm italic text-center py-4">No schedule data.</p>
              : schedule.map((s, i) => (
                <div key={i} className="flex items-center justify-between text-xs rounded-lg border border-white/5 bg-white/3 px-3 py-2">
                  <span className="text-white/50">{s.month}</span>
                  <span className="text-white/60">EMI: {fmt(s.emi)}</span>
                  <span className="text-white/40">Principal: {fmt(s.principal)}</span>
                  <span className="text-white/40">Interest: {fmt(s.interest)}</span>
                  <span className={s.paid ? "text-[#00ff90]/60" : "text-white/25"}>{s.paid ? "Paid" : "Pending"}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}